import type { ComponentPropsWithoutRef, CSSProperties, ReactNode } from 'react';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
}
export function Button({ variant = 'primary', size = 'md', className, ...rest }: ButtonProps) {
  return (
    <button
      className={cx('dsp-btn', `dsp-btn--${variant}`, `dsp-btn--${size}`, className)}
      {...rest}
    />
  );
}

export function Input({ className, ...rest }: ComponentPropsWithoutRef<'input'>) {
  return <input className={cx('dsp-input', className)} {...rest} />;
}

export function Textarea({ className, ...rest }: ComponentPropsWithoutRef<'textarea'>) {
  return <textarea className={cx('dsp-textarea', className)} {...rest} />;
}

export function Select({ className, ...rest }: ComponentPropsWithoutRef<'select'>) {
  return <select className={cx('dsp-select', className)} {...rest} />;
}

export interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string | null;
  children: ReactNode;
}
export function Field({ label, htmlFor, error, children }: FieldProps) {
  if (!htmlFor) {
    return (
      <label className="dsp-field">
        <span className="dsp-field__label">{label}</span>
        {children}
        {error ? <span className="dsp-field__error">{error}</span> : null}
      </label>
    );
  }
  return (
    <div className="dsp-field">
      <label className="dsp-field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? <span className="dsp-field__error">{error}</span> : null}
    </div>
  );
}

export function Card({ className, ...rest }: ComponentPropsWithoutRef<'div'>) {
  return <div className={cx('dsp-card', className)} {...rest} />;
}

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';
export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={cx('dsp-badge', `dsp-badge--${tone}`)}>{children}</span>;
}

export interface StackProps {
  gap?: number;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}
export function Stack({ gap = 12, className, style, children }: StackProps) {
  return (
    <div className={cx('dsp-stack', className)} style={{ gap: `${gap}px`, ...style }}>
      {children}
    </div>
  );
}

export function Row({ gap = 12, className, style, children }: StackProps) {
  return (
    <div
      className={cx('dsp-row', className)}
      style={{ gap: `${gap}px`, flexWrap: 'wrap', ...style }}
    >
      {children}
    </div>
  );
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}
export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <div className="dsp-page-header">
      <div>
        <h1 className="dsp-page-header__title">{title}</h1>
        {subtitle ? <div className="dsp-page-header__subtitle">{subtitle}</div> : null}
      </div>
      {actions ? (
        <div className="dsp-row" style={{ gap: 8 }}>
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: string;
  action?: ReactNode;
}
export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="dsp-empty">
      <p style={{ fontWeight: 600, color: 'var(--dsp-text)' }}>{title}</p>
      {description ? <p>{description}</p> : null}
      {action ? <div style={{ marginTop: '1rem' }}>{action}</div> : null}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="dsp-row" style={{ gap: 10, padding: '2rem', justifyContent: 'center' }}>
      <span className="dsp-spinner" aria-hidden />
      {label ? <span className="dsp-muted">{label}</span> : null}
    </div>
  );
}

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  itemLabel?: string;
  disabled?: boolean;
  onPageChange: (page: number) => void;
}

/** Accessible, compact pagination for any server-backed list. */
export function Pagination({
  page,
  pageSize,
  total,
  totalPages,
  itemLabel = 'items',
  disabled = false,
  onPageChange,
}: PaginationProps) {
  if (total === 0) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <nav className="dsp-pagination" aria-label={`${itemLabel} pagination`}>
      <span className="dsp-pagination__summary" aria-live="polite">
        {first}–{last} of {total} {itemLabel}
      </span>
      <div className="dsp-row" style={{ gap: 8 }}>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={disabled || page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <span className="dsp-pagination__page">
          Page {page} of {Math.max(totalPages, 1)}
        </span>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={disabled || page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </nav>
  );
}

/** Single shimmer block. `w`/`h` accept any CSS length; defaults suit a line of text. */
export function Skeleton({
  w = '100%',
  h = 14,
  radius,
  style,
}: {
  w?: string | number;
  h?: string | number;
  radius?: string | number;
  style?: CSSProperties;
}) {
  return (
    <span
      className="dsp-skeleton"
      aria-hidden
      style={{ width: w, height: h, borderRadius: radius ?? 6, ...style }}
    />
  );
}

/** A shimmer placeholder shaped like a Card, for list/detail loading states. */
export function SkeletonCard({ lines = 2 }: { lines?: number }) {
  return (
    <div className="dsp-card" aria-hidden style={{ padding: '1.1rem 1.25rem' }}>
      <div className="dsp-row" style={{ justifyContent: 'space-between' }}>
        <Skeleton w={140} h={16} />
        <Skeleton w={72} h={22} radius={999} />
      </div>
      <div
        className="dsp-stack"
        style={{ gap: 10, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--dsp-border)' }}
      >
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton key={i} w={i === lines - 1 ? '55%' : '80%'} />
        ))}
      </div>
    </div>
  );
}

/** Gold "Demo data" pill — marks any view/figure that is illustrative, not from the live backend. */
export function DemoBadge({ label = 'Demo data' }: { label?: string }) {
  return <span className="dsp-demo-badge">{label}</span>;
}

/** Banner for the top of a demo screen: states plainly that the screen is a preview, not live. */
export function DemoBanner({ children }: { children?: ReactNode }) {
  return (
    <div className="dsp-demo-banner" role="note">
      <DemoBadge label="Demo" />
      <span>
        {children ??
          'This screen is a design preview with sample data — it is not yet wired to the live backend.'}
      </span>
    </div>
  );
}

/** N stacked SkeletonCards — drop-in replacement for a list <Spinner/> while data loads. */
export function SkeletonList({ count = 3 }: { count?: number }) {
  return (
    <div className="dsp-stack" style={{ gap: 12 }} role="status" aria-label="Loading">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}
